// src/components/AuthDebugPanel.jsx
// Temporary auth diagnostics panel for Loop.
// Console diagnostics are available through window.loopAuthDebug().
// Visual panel is DEVELOPMENT-ONLY and appears only when enabled with ?debugAuth=1,
// localStorage loop_auth_debug_enabled=1, or window.__LOOP_AUTH_DEBUG__ = true.

import React, { useEffect, useMemo, useState } from "react";
import { isIosCapacitor } from "../utils/nativeHttp";
import { getManualAuth, getEffectiveManualAuthSession, isManualAuthLikelyValid } from "../utils/manualAuth";
import { getCurrentEffectiveSession, isEffectiveSessionLoggedIn } from "../auth/effectiveSession";
import { listStorageKeys } from "../auth/authStorage";

function safeJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return String(value || "");
  }
}

function maskToken(token) {
  const raw = String(token || "");
  if (!raw) return "";
  if (raw.length <= 18) return `${raw.slice(0, 4)}…${raw.slice(-4)}`;
  return `${raw.slice(0, 10)}…${raw.slice(-8)}`;
}

function isProductionRuntime() {
  try {
    return process.env.NODE_ENV === "production";
  } catch (_) {
    return false;
  }
}

function getDebugRequestedFromRuntime() {
  try {
    if (typeof window === "undefined") return false;
    if (window.__LOOP_AUTH_DEBUG__ === true) return true;
    const params = new URLSearchParams(window.location.search || "");
    if (params.get("debugAuth") === "1") return true;
    if (window.localStorage?.getItem("loop_auth_debug_enabled") === "1") return true;
  } catch (_) {}
  return false;
}

function getVisualDebugEnabledFromRuntime() {
  // Option B: visual diagnostics are never rendered in production builds.
  // The console helper window.loopAuthDebug() is still registered below.
  if (isProductionRuntime()) return false;
  return getDebugRequestedFromRuntime();
}

function getStorageSummary() {
  const localKeys = listStorageKeys(typeof localStorage !== "undefined" ? localStorage : null);
  const sessionKeys = listStorageKeys(typeof sessionStorage !== "undefined" ? sessionStorage : null);
  const interesting = [
    "__MANUAL_AUTH__",
    "loop_manual_auth",
    "loop_native_auth_restore_allowed_v1",
    "loop_auth_debug_enabled",
    "pending_uid",
    "pending_email",
    "loop_profile_fast_cache_v1:",
    "loop_user_businesses_roles_v3:",
    "loop:businessAccess:",
    "loop_mainmenu_favorites_docs_v1",
    "loop_mainmenu_calendar_access_v2",
    "loop_worker_businesses_modal_cache_v3:",
    "loop_booking_toast_seen_v1",
    "loop_notification_center_filters_v2",
    "loop_push_registration_state_v1",
    "loop_fcm_token_v1",
    "loop_web_fcm_token_v1",
    "firebase:authUser:",
  ];

  const isInteresting = (key) => interesting.some((needle) => String(key).startsWith(needle) || String(key) === needle);
  return {
    localCount: localKeys.length,
    sessionCount: sessionKeys.length,
    localAuthKeys: localKeys.filter(isInteresting).sort(),
    sessionLoopKeys: sessionKeys.filter((k) => String(k).startsWith("loop_") || String(k).startsWith("intent_")).sort(),
  };
}

function buildAuthDebugReport({ auth, user, userInfo, authSessionKey }) {
  const firebaseCurrentUser = (() => {
    try {
      const u = auth?.currentUser || null;
      if (!u) return null;
      return {
        uid: u.uid || null,
        email: u.email || "",
        displayName: u.displayName || "",
        emailVerified: u.emailVerified === true,
        providerIds: Array.isArray(u.providerData) ? u.providerData.map((p) => p?.providerId).filter(Boolean) : [],
      };
    } catch (_) {
      return null;
    }
  })();

  const manualAuth = (() => {
    try {
      const ma = getManualAuth();
      if (!ma) return null;
      return {
        uid: ma.uid || null,
        email: ma.email || "",
        displayName: ma.displayName || ma.name || "",
        source: ma.source || "manual-auth",
        valid: isManualAuthLikelyValid(ma),
        hasIdToken: Boolean(ma.idToken),
        idTokenPreview: maskToken(ma.idToken),
        obtainedAt: ma.obtainedAt || null,
        expiresIn: ma.expiresIn || null,
      };
    } catch (_) {
      return null;
    }
  })();

  const manualSession = (() => {
    try {
      const session = getEffectiveManualAuthSession();
      return {
        loggedIn: session?.loggedIn === true,
        source: session?.source || "",
        user: session?.user || null,
      };
    } catch (_) {
      return null;
    }
  })();

  const effectiveSession = (() => {
    try {
      const session = getCurrentEffectiveSession({
        firebaseAuth: auth || null,
        userInfo: userInfo || {},
        isIosRuntime: isIosCapacitor(),
      });
      return {
        loggedIn: isEffectiveSessionLoggedIn(session),
        source: session?.source || "guest",
        sessionKey: session?.sessionKey || "guest",
        user: session?.user || null,
        headerUserInfo: session?.headerUserInfo || null,
        hasFirebaseUser: Boolean(session?.firebaseUser),
        hasManualAuth: Boolean(session?.manualAuth),
      };
    } catch (error) {
      return { loggedIn: false, source: "debug-error", error: String(error?.message || error) };
    }
  })();

  return {
    generatedAt: new Date().toISOString(),
    platform: (() => {
      try {
        const cap = window.Capacitor || null;
        return cap?.getPlatform?.() || cap?.platform || "web";
      } catch (_) {
        return "web";
      }
    })(),
    isIosCapacitor: isIosCapacitor(),
    authSessionKey: authSessionKey || "",
    propUser: user ? { uid: user.uid || null, email: user.email || "", displayName: user.displayName || "" } : null,
    firebaseCurrentUser,
    manualAuth,
    manualSession,
    effectiveSession,
    storage: getStorageSummary(),
    lastAuthEvent: (() => {
      try { return window.__LOOP_AUTH_DEBUG_LAST_EVENT__ || null; } catch (_) { return null; }
    })(),
    lastPushAction: (() => {
      try { return window.__LOOP_PUSH_LAST_NOTIFICATION_ACTION__ || null; } catch (_) { return null; }
    })(),
  };
}

export default function AuthDebugPanel({ auth, user, userInfo, authSessionKey }) {
  const [enabled, setEnabled] = useState(() => getVisualDebugEnabledFromRuntime());
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const eventNames = [
      "loop-auth-login-success",
      "manual-auth-changed",
      "manual-auth-detected",
      "manual-auth-cleared",
      "loop-auth-cleared",
      "loop-auth-storage-scope-changed",
      "storage",
    ];

    const handler = (event) => {
      try {
        window.__LOOP_AUTH_DEBUG_LAST_EVENT__ = {
          type: event?.type || "unknown",
          detail: event?.detail || null,
          key: event?.key || null,
          time: new Date().toISOString(),
        };
      } catch (_) {}
      setEnabled(getVisualDebugEnabledFromRuntime());
      setTick((v) => v + 1);
    };

    eventNames.forEach((name) => window.addEventListener(name, handler));

    window.loopAuthDebug = () => {
      const report = buildAuthDebugReport({ auth, user, userInfo, authSessionKey });
      try { console.table(report.effectiveSession || {}); } catch (_) {}
      try { console.log("[Loop Auth Debug]", report); } catch (_) {}
      return report;
    };

    window.loopAuthDebugEnable = () => {
      try { window.__LOOP_AUTH_DEBUG__ = true; } catch (_) {}
      // Production hardening: never persist a visual debug-panel flag in production.
      // The console helper remains available, but the floating UI stays hidden.
      try {
        if (!isProductionRuntime()) {
          window.localStorage?.setItem("loop_auth_debug_enabled", "1");
        } else {
          window.localStorage?.removeItem("loop_auth_debug_enabled");
        }
      } catch (_) {}
      const next = getVisualDebugEnabledFromRuntime();
      setEnabled(next);
      setTick((v) => v + 1);
      return {
        visualPanelAllowed: !isProductionRuntime(),
        report: typeof window.loopAuthDebug === "function" ? window.loopAuthDebug() : null,
      };
    };

    window.loopAuthDebugDisable = () => {
      try { window.__LOOP_AUTH_DEBUG__ = false; } catch (_) {}
      try { window.localStorage?.removeItem("loop_auth_debug_enabled"); } catch (_) {}
      setEnabled(false);
      return { disabled: true, visualPanelAllowed: !isProductionRuntime() };
    };

    return () => {
      eventNames.forEach((name) => window.removeEventListener(name, handler));
    };
  }, [auth, user, userInfo, authSessionKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => setTick((v) => v + 1), 2000);
    return () => clearInterval(timer);
  }, [enabled]);

  const report = useMemo(
    () => buildAuthDebugReport({ auth, user, userInfo, authSessionKey }),
    [auth, user, userInfo, authSessionKey, tick]
  );

  if (!enabled || isProductionRuntime()) return null;

  const copyReport = async () => {
    const text = safeJson(report);
    setCopyStatus("");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopyStatus("Copiado");
      } else {
        console.log("[Loop Auth Debug Copy]", report);
        setCopyStatus("Impreso en consola");
      }
    } catch (_) {
      console.log("[Loop Auth Debug Copy]", report);
      setCopyStatus("Impreso en consola");
    }
  };

  const disable = () => {
    try { window.loopAuthDebugDisable?.(); } catch (_) {}
    try { window.__LOOP_AUTH_DEBUG__ = false; } catch (_) {}
    try { localStorage.removeItem("loop_auth_debug_enabled"); } catch (_) {}
    setEnabled(false);
  };

  const source = report?.effectiveSession?.source || "guest";
  const loggedIn = report?.effectiveSession?.loggedIn === true;

  return (
    <div style={styles.root} aria-live="polite">
      <button type="button" style={styles.fab} onClick={() => setOpen((v) => !v)}>
        <span style={{ ...styles.dot, background: loggedIn ? "#10b981" : "#f59e0b" }} />
        Auth Debug
      </button>

      {open ? (
        <section style={styles.panel} aria-label="Loop Auth Debug Panel">
          <div style={styles.header}>
            <div>
              <div style={styles.kicker}>Loop diagnostics</div>
              <h3 style={styles.title}>Auth Debug</h3>
            </div>
            <button type="button" style={styles.close} onClick={() => setOpen(false)}>×</button>
          </div>

          <div style={styles.grid}>
            <Info label="Logged in" value={loggedIn ? "true" : "false"} />
            <Info label="Source" value={source} />
            <Info label="UID" value={report?.effectiveSession?.user?.uid || "—"} />
            <Info label="Email" value={report?.effectiveSession?.user?.email || "—"} />
            <Info label="Firebase user" value={report?.firebaseCurrentUser ? "exists" : "missing"} />
            <Info label="Manual auth" value={report?.manualAuth ? (report.manualAuth.valid ? "valid" : "invalid") : "missing"} />
            <Info label="Platform" value={report?.platform || "web"} />
            <Info label="Session key" value={report?.effectiveSession?.sessionKey || "guest"} />
          </div>

          <details style={styles.details}>
            <summary style={styles.summary}>Storage keys</summary>
            <pre style={styles.pre}>{safeJson(report.storage)}</pre>
          </details>

          <details style={styles.details}>
            <summary style={styles.summary}>Full report</summary>
            <pre style={styles.pre}>{safeJson(report)}</pre>
          </details>

          <div style={styles.actions}>
            <button type="button" style={styles.primary} onClick={copyReport}>Copy debug report</button>
            <button type="button" style={styles.secondary} onClick={() => setTick((v) => v + 1)}>Refresh</button>
            <button type="button" style={styles.secondary} onClick={disable}>Disable</button>
          </div>
          {copyStatus ? <div style={styles.copyStatus}>{copyStatus}</div> : null}
        </section>
      ) : null}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={styles.info}>
      <span style={styles.infoLabel}>{label}</span>
      <strong style={styles.infoValue}>{String(value || "—")}</strong>
    </div>
  );
}

const styles = {
  root: {
    position: "fixed",
    right: "max(14px, env(safe-area-inset-right, 0px))",
    bottom: "max(14px, env(safe-area-inset-bottom, 0px))",
    zIndex: 2147483000,
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  },
  fab: {
    height: 42,
    border: 0,
    borderRadius: 999,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#0f172a",
    color: "#ffffff",
    boxShadow: "0 18px 40px rgba(15,23,42,.28)",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  dot: { width: 8, height: 8, borderRadius: 999, display: "inline-block" },
  panel: {
    position: "absolute",
    right: 0,
    bottom: 52,
    width: "min(430px, calc(100vw - 28px))",
    maxHeight: "min(680px, calc(100dvh - 96px))",
    overflow: "auto",
    borderRadius: 22,
    border: "1px solid rgba(226,232,240,.95)",
    background: "rgba(255,255,255,.98)",
    boxShadow: "0 24px 70px rgba(15,23,42,.24)",
    padding: 14,
    color: "#0f172a",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 },
  kicker: { fontSize: 10, fontWeight: 950, letterSpacing: ".12em", textTransform: "uppercase", color: "#00897f" },
  title: { margin: "3px 0 0", fontSize: 18, lineHeight: 1.1, fontWeight: 950 },
  close: { width: 34, height: 34, borderRadius: 999, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 22, lineHeight: 1 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  info: { border: "1px solid #e2e8f0", borderRadius: 14, padding: 10, background: "#f8fafc", minWidth: 0 },
  infoLabel: { display: "block", fontSize: 10, fontWeight: 950, color: "#64748b", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 },
  infoValue: { display: "block", fontSize: 12, fontWeight: 900, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  details: { marginTop: 10, border: "1px solid #e2e8f0", borderRadius: 14, padding: "8px 10px", background: "#fff" },
  summary: { cursor: "pointer", fontSize: 12, fontWeight: 900, color: "#0f172a" },
  pre: { maxHeight: 220, overflow: "auto", margin: "8px 0 0", padding: 10, borderRadius: 10, background: "#0f172a", color: "#d1fae5", fontSize: 11, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  actions: { display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" },
  primary: { border: 0, borderRadius: 999, background: "#00675f", color: "#fff", height: 34, padding: "0 12px", fontSize: 12, fontWeight: 900, cursor: "pointer" },
  secondary: { border: "1px solid #dbe5ee", borderRadius: 999, background: "#fff", color: "#0f172a", height: 34, padding: "0 12px", fontSize: 12, fontWeight: 900, cursor: "pointer" },
  copyStatus: { marginTop: 8, fontSize: 12, color: "#00897f", fontWeight: 850 },
};
